import type { DayOffPriority, OfferedShiftType, ShiftPreferences, ShiftTypeKey, ShiftTypePreference } from '../types.js'

export const SHIFT_TYPE_LABELS: Record<ShiftTypeKey, string> = { day: 'DAY', evening: 'EVE', night: 'NOC' }
export const SHIFT_TYPE_WORDS: Record<ShiftTypeKey, string> = { day: 'Day', evening: 'Evening', night: 'Night' }
export const PRIORITY_LABELS: Record<DayOffPriority, string> = { high: 'High', medium: 'Medium', low: 'Low' }
export const PRIORITY_CLASS: Record<DayOffPriority, string> = { high: 'bg-coral text-white border-coral', medium: 'bg-gold/30 text-ink border-gold', low: 'bg-green/15 text-green border-green/50' }
/** Eight hours a shift, as the demo counts them. */
export const HOURS_PER_SHIFT = 8

/** "7 AM – 3 PM" from "07:00" and "15:00". */
export function describeTimes(startTime: string, endTime: string): string {
  const clock = (value: string): string => {
    const [h = '0', m = '00'] = value.split(':')
    const hour = Number(h)
    const suffix = hour >= 12 ? 'PM' : 'AM'
    const twelve = hour % 12 === 0 ? 12 : hour % 12
    return m === '00' ? `${twelve} ${suffix}` : `${twelve}:${m} ${suffix}`
  }
  return `${clock(startTime)} – ${clock(endTime)}`
}

/** "1. DAY · 2. EVE · never NOC". */
export function describeShiftOrder(shiftTypes: readonly ShiftTypePreference[]): string {
  const wanted = shiftTypes.filter((entry) => entry.willDo).map((entry, index) => `${index + 1}. ${SHIFT_TYPE_LABELS[entry.shiftType]}`)
  const never = shiftTypes.filter((entry) => !entry.willDo).map((entry) => `never ${SHIFT_TYPE_LABELS[entry.shiftType]}`)
  return [...wanted, ...never].join(' · ')
}

/** "128–160 hrs (16–20 shifts)". */
export function describeHoursRange(hours: { min: number; max: number }): string {
  const shifts = (value: number): number => Math.round(value / HOURS_PER_SHIFT)
  return hours.min === hours.max ? `${hours.min} hrs (${shifts(hours.min)} shifts)` : `${hours.min}–${hours.max} hrs (${shifts(hours.min)}–${shifts(hours.max)} shifts)`
}

/** "March 2026" from "2026-03". */
export function describePeriod(period: string): string {
  const [year, month] = period.split('-').map(Number)
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** "Mar 15" from "2026-03-15", read at noon so no zone moves it. */
export function describeDay(date: string): string {
  const parsed = new Date(`${date}T12:00:00`)
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** Every date of the month, `YYYY-MM-DD`. */
export function datesOf(period: string): string[] {
  const [year, month] = period.split('-').map(Number)
  const days = new Date(Date.UTC(year ?? 1970, month ?? 1, 0)).getUTCDate()
  return Array.from({ length: days }, (_, index) => `${period}-${String(index + 1).padStart(2, '0')}`)
}

/** The weekday (0 Sunday) the month starts on. */
export function firstWeekdayOf(period: string): number {
  const [year, month] = period.split('-').map(Number)
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, 1)).getUTCDay()
}

/** The order the form starts in when nothing is saved: the facility's offered types, all wanted. */
export function defaultShiftTypes(offered: readonly OfferedShiftType[]): ShiftTypePreference[] {
  return offered.map((entry) => ({ shiftType: entry.shiftType, willDo: true }))
}

/** One line per thing chosen, for a read-only view and the review step. */
export function summarizePreferences(preferences: Pick<ShiftPreferences, 'shiftTypes' | 'hours' | 'ptoDates' | 'daysOff' | 'note'> & { availableDates?: string[] | null }): { label: string; value: string }[] {
  const byPriority = (priority: DayOffPriority): string[] => preferences.daysOff.filter((entry) => entry.priority === priority).map((entry) => describeDay(entry.date))
  const available = preferences.availableDates ?? null
  const lines = [
    { label: 'Shift order', value: describeShiftOrder(preferences.shiftTypes) },
    { label: 'Hours', value: describeHoursRange(preferences.hours) },
    { label: 'PTO', value: preferences.ptoDates.length === 0 ? 'None requested' : preferences.ptoDates.map(describeDay).join(', ') },
    // A worker who marks the days they can work has no days off: every other day is untouched.
    available !== null
      ? { label: 'Can work', value: available.length === 0 ? 'No days marked' : `${available.length} ${available.length === 1 ? 'day' : 'days'}: ${available.map(describeDay).join(', ')}` }
      : { label: 'Days off', value: preferences.daysOff.length === 0 ? 'None marked' : (['high', 'medium', 'low'] as const).flatMap((priority) => (byPriority(priority).length ? [`${PRIORITY_LABELS[priority]}: ${byPriority(priority).join(', ')}`] : [])).join(' · ') },
  ]
  return preferences.note ? [...lines, { label: 'Note', value: preferences.note }] : lines
}
